import type { ReactNode } from 'react';

import { Icon } from '@breezeventures/ui-kit/next/icon';
import { Typography } from '@breezeventures/ui-kit/typography';
import cn from 'classnames';

export type InlineAlertType = 'info' | 'success' | 'warning' | 'error';

interface Props {
  type: InlineAlertType;
  title?: string;
  children?: React.ReactNode;
  showIcon?: boolean;
  className?: string;
}

const typeConfig: Record<
  InlineAlertType,
  {
    bgClass: string;
    accentClass: string;
    iconClass: string;
    icon: ReactNode;
  }
> = {
  info: {
    bgClass: 'bg-info',
    accentClass: 'bg-info-accent',
    iconClass: 'text-info-accent',
    icon: (
      <Icon
        name="alert-circle"
        size={24}
        version="v1"
        style="filled"
        className="size-5"
      />
    ),
  },
  success: {
    bgClass: 'bg-positive-1',
    accentClass: 'bg-positive-accent',
    iconClass: 'text-positive-accent',
    icon: (
      <Icon
        name="check-circle"
        size={24}
        version="v1"
        style="filled"
        className="size-5"
      />
    ),
  },
  warning: {
    bgClass: 'bg-warning',
    accentClass: 'bg-warning-accent',
    iconClass: 'text-warning-accent',
    icon: (
      <Icon name="alert-triangle" size={24} version="v1" className="size-5" />
    ),
  },
  error: {
    bgClass: 'bg-negative-1',
    accentClass: 'bg-negative-accent',
    iconClass: 'text-negative-accent',
    icon: (
      <Icon
        name="cross-circle"
        size={24}
        version="v1"
        style="filled"
        className="size-5"
      />
    ),
  },
};

export const InlineAlert = ({
  type,
  title,
  children,
  showIcon = true,
  className,
}: Props) => {
  const { bgClass, accentClass, iconClass, icon } = typeConfig[type];

  return (
    <div
      role="alert"
      className={cn(
        'relative flex items-start gap-3 overflow-hidden rounded-2xl px-4 py-3',
        bgClass,
        className
      )}
    >
      <span
        aria-hidden="true"
        className={cn('absolute inset-y-0 inset-s-0 w-1', accentClass)}
      />
      {showIcon && (
        <span
          aria-hidden="true"
          className={cn(
            'flex shrink-0 items-center',
            title ? 'h-6' : 'h-5',
            iconClass
          )}
        >
          {icon}
        </span>
      )}
      <div className="flex flex-col gap-1">
        {title && (
          <Typography.Text tag="div" size="m" weight="medium">
            {title}
          </Typography.Text>
        )}
        {children && (
          <Typography.Text tag="div" size="s">
            {children}
          </Typography.Text>
        )}
      </div>
    </div>
  );
};
